import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button, TextField, CircularProgress } from "@mui/material";
import MainPage from "../MainPage";

/**
 * AIBoomPage — /highlights/ai-boom
 *
 * Public opinion on the AI build-out, for clients who need to know how the
 * boom is landing rather than how the technology works. Built on the
 * prediction-markets reel's architecture: minimal chrome, two overlay heroes
 * bookending four content slides, one idea per card.
 *
 * ── THE SPINE ────────────────────────────────────────────────────────────────
 * The closer AI gets to you, the less you like it. That is one line across all
 * four question sets, and every step of it is measured:
 *   abstract, for me    AI will improve my own life        net +14.1
 *   collective          AI will improve society            net  -3.1
 *   physical            a data center in my area           net  -7.1
 *   commercial          an ad I liked turns out to be AI    52.8% trust less
 *
 * ── PROVENANCE, AND THE TIMING POINT THAT IS EASY TO GET WRONG ───────────────
 * Figures are weighted by `total` off the tracking and ACC tables, computed
 * through the app's own loader so they match what the map draws. MRP estimates,
 * n>8,000 per wave.
 *
 * The wave NUMBERS differ between the two schemas but the FIELDWORK DOES NOT.
 * ACC "wave 3" was fielded alongside tracking "wave 4" (July 2026). So the
 * data-center answers and the wave-4 AI answers describe the same moment, and
 * the four-step comparison above is a genuine single snapshot rather than a
 * splice. Do not add a caveat saying otherwise. The only thing that cannot be
 * done is putting them on one wave axis, because the numbering disagrees.
 *
 * District alignment was verified rather than assumed: the ACC and tracking CD
 * tables carry the same 436 districts and both are projected on CD120, so the
 * cross-schema district comparison on the data-center slide is like-for-like.
 * (Tracking carries one extra all-null DC placeholder row, dist_fips 1100;
 * every district count here excludes it.)
 *
 * ── THE TRACKED SERIES: THREE WAVES, AND A GAP THAT WILL NOT CLOSE ───────────
 * AI sentiment is the only genuinely tracked series in this set. Net is
 * "mostly better" minus "mostly worse" over the next five years.
 *              wave 2   wave 3   wave 4
 *   your life    +8.0     +6.4    +14.1
 *   society      -9.0    -12.1     -3.1
 *   gap          17.0     18.5     17.2
 * Both measures jumped in wave 4 and society is still net negative. The
 * interesting part is the GAP: sentiment moved a lot, the distance between
 * "good for me" and "good for us" did not move at all. It has sat at 17 to 18
 * points in every wave.
 *
 * NOTE: ai_aggregators used to declare these groups as waves [2,3]. The wave-4
 * columns were always in the tracking tables, so a whole wave of a tracked
 * series was hidden. Exposing it is what makes this reel possible.
 *
 * ── THE GENERATIONS ARE COMING APART ─────────────────────────────────────────
 * Net on AI for society, by age:
 *              wave 2   wave 3   wave 4    shift
 *   18-34        -7.2     -1.3    +12.6    +19.8
 *   35-64        -5.8     -9.9     +2.5     +8.3
 *   65+         -16.2    -24.1    -25.1     -8.9
 * Under-35s crossed from net negative to net positive; over-65s got worse. The
 * generational gap went from 9.0 points to 37.7 in three waves.
 *
 * On AI for YOUR OWN LIFE the district distributions do not overlap AT ALL:
 * every district sits at +16.3 or better among under-35s and at +7.9 or worse
 * among over-65s. Two populations, no shared ground.
 *
 * The mechanism is usage, not opinion. Daily AI use runs 62.0% among under-35s
 * against 17.2% among over-65s, 61.0% of whom rarely or never touch it.
 *
 * ── FINDINGS COMPUTED BUT NOT ON THE REEL ────────────────────────────────────
 * Kept because they are verified and will come up in the room.
 *   Nationally 40.2% use AI daily, 24.0% weekly, 35.7% rarely or never. So 64%
 *   use it weekly or more, which is higher than most audiences assume.
 *   Reasons to oppose data centers nationally: environmental impact 29.8%,
 *   energy prices 20.6%, distrust of the companies 13.2%, noise 12.5%, wanting
 *   to slow AI expansion 10.9%, land use 7.2%, construction disruption 5.8%.
 *   The two leading reasons are both material rather than ideological, which
 *   matters for how a siting argument gets made.
 *   Data-center support is net positive nationally in 433 of 436 districts, and
 *   net negative locally in 390. The overlap, positive nationally AND negative
 *   locally, is 387 districts: 89% of the country.
 *   Northern Virginia inverts the naive NIMBY read. VA-10 and VA-11, the actual
 *   data-center heartland, are the only Virginia districts still net POSITIVE
 *   locally (+4.6 and +4.4) while the rest of the state is negative. Proximity
 *   to existing build-out tracks with support, not opposition.
 */

// ─── Data constants (see PROVENANCE header) ───────────────────────────────────
const WAVE_LABEL = { 2: "Wave 2", 3: "Wave 3", 4: "Wave 4" };

// The four-step spine, in order of how close AI gets.
const PROXIMITY = [
  { label: "AI will improve my own life",           value: "+14.1", tone: "#59a14f", note: "net, over the next five years" },
  { label: "AI will improve society",               value: "−3.1",  tone: "#e15759", note: "net, same question, same people" },
  { label: "A data center in my area",              value: "−7.1",  tone: "#e15759", note: "net support, local" },
  { label: "An ad I liked turns out to be AI",      value: "52.8%", tone: "#C1121F", note: "would trust the brand less" },
];

// Tracked series. Shares are [better, not much different, worse, don't know].
const AI_TREND = {
  life: {
    label: "Your own life",
    color: "#59a14f",
    shares: { 2: [28.5, 38.5, 20.4, 12.6], 3: [28.0, 40.0, 21.6, 10.4], 4: [33.5, 37.7, 19.4, 9.4] },
    nets: { 2: 8.0, 3: 6.4, 4: 14.1 },
  },
  society: {
    label: "Society as a whole",
    color: "#4e79a7",
    shares: { 2: [26.5, 25.0, 35.5, 13.0], 3: [26.8, 22.9, 38.9, 11.3], 4: [31.3, 24.3, 34.4, 10.0] },
    nets: { 2: -9.0, 3: -12.1, 4: -3.1 },
  },
};
const TREND_LABELS = ["Mostly better", "Not much different", "Mostly worse", "Don't know"];

/**
 * The four segments of the trend bar, in share order.
 *
 * `ink` is the colour a number printed ON that segment takes. "Don't know" is
 * deliberately the palest of the four and carries dark text instead of white:
 * it is a non-answer, it is excluded from the net, and it should recede rather
 * than compete with the three real answers.
 *
 * The two middles used to be #bab0ab and #d3d3d3, a warm grey and a light grey
 * close enough that "not much different" and "don't know" read as one band.
 */
const TREND_SEGMENTS = [
  { color: "#59a14f", ink: "#ffffff" },
  { color: "#b4b9c2", ink: "#ffffff" },
  { color: "#e15759", ink: "#ffffff" },
  { color: "#eceef1", ink: "rgba(15,23,42,0.6)" },
];

/**
 * One key for all three bars, replacing the four labels that used to be printed
 * as a run-on string above every wave: the same nineteen words, three times
 * over, at 10px, with nothing tying any of them to a colour.
 */
function TrendLegend() {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: { xs: 1, sm: 1.4 }, mb: 1 }}>
      {TREND_LABELS.map((label, i) => (
        <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: 9, height: 9, borderRadius: "2px", bgcolor: TREND_SEGMENTS[i].color, border: i === 3 ? "1px solid rgba(15,23,42,0.12)" : "none", flexShrink: 0 }} />
          <Typography sx={{ fontSize: { xs: "10.5px", sm: "10px" }, fontWeight: 600, color: MUTED, fontFamily: F, whiteSpace: "nowrap" }}>
            {label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

// District distributions of NET, one row per age band on a shared axis. Same
// small-multiples treatment as the prediction-markets reel: position compares
// the groups, the box and whiskers show every district's spread.
// Entries are [min, p25, median, p75, max] for wave 4, plus earlier medians.
const AI_DIST = {
  society: {
    axis: [-36, 32],
    rows: [
      { name: "All adults", ref: true, jul: [-14.2, -6.1, -3.2, -0.5, 13.3], w3Med: -11.8, w2Med: -9.2 },
      { name: "18–34", filter: { age: 1 }, jul: [-7.1, 9.1, 13.2, 16.0, 29.1], w3Med: -1.3, w2Med: -7.8 },
      { name: "35–64", filter: { age: 2 }, jul: [-9.5, -0.4, 2.5, 5.0, 14.5], w3Med: -9.6, w2Med: -5.8 },
      { name: "65+", filter: { age: 3 }, jul: [-33.2, -27.9, -25.1, -22.6, -5.6], w3Med: -23.7, w2Med: -16.4 },
    ],
    note: "Under-35s crossed from net negative to net positive on AI for society between waves. Over-65s moved the other way and are now at −25. The two distributions overlap by barely a point.",
  },
  life: {
    axis: [-16, 48],
    rows: [
      { name: "All adults", ref: true, jul: [3.4, 10.9, 13.5, 17.0, 32.6], w3Med: 6.4, w2Med: 7.8 },
      { name: "18–34", filter: { age: 1 }, jul: [16.3, 25.6, 27.7, 30.8, 45.9], w3Med: 14.6, w2Med: 14.1 },
      { name: "35–64", filter: { age: 2 }, jul: [8.2, 15.9, 18.5, 21.7, 37.6], w3Med: 11.6, w2Med: 10.9 },
      { name: "65+", filter: { age: 3 }, jul: [-13.7, -8.3, -5.8, -2.7, 7.9], w3Med: -9.4, w2Med: -2.2 },
    ],
    note: "On AI for your own life the distributions do not overlap at all. Every district is at +16.3 or better among under-35s and at +7.9 or worse among over-65s. There is no shared ground between them.",
  },
};

// Wave 4. Usage is the mechanism behind the sentiment split.
const AI_USE = {
  national: [
    { key: "model_ai_use_daily_pred_n", label: "Daily", pct: 40.2, color: "#2E7D32" },
    { key: "model_ai_use_weekly_pred_n", label: "Weekly or monthly", pct: 24.0, color: "#8cd17d" },
    { key: "model_ai_use_rarely_never_pred_n", label: "Rarely or never", pct: 35.7, color: "#bab0ab" },
  ],
  byAge: [
    { label: "18–34", filter: { age: 1 }, daily: 62.0, rarely: 15.0 },
    { label: "35–64", filter: { age: 2 }, daily: 44.1, rarely: 30.2 },
    { label: "65+",   filter: { age: 3 }, daily: 17.2, rarely: 61.0 },
  ],
};

// Data centers. ACC schema, same fieldwork as tracking wave 4.
const DC = {
  national: { support: 41.5, neither: 26.7, oppose: 31.8, net: 9.6 },
  local:    { support: 33.7, neither: 25.6, oppose: 40.8, net: -7.1 },
  gap: 16.8,
  posNationally: 433,
  negLocally: 390,
  bothCount: 387,
  ofDistricts: 436,
  gapByAge: [
    { label: "18–34", nat: 9.4, loc: -2.4 },
    { label: "35–64", nat: 14.4, loc: -2.7 },
    { label: "65+",   nat: 1.2, loc: -18.6 },
  ],
  reasons: [
    { label: "Environmental impact", pct: 29.8 },
    { label: "Energy prices", pct: 20.6 },
    { label: "Distrust of the companies", pct: 13.2 },
    { label: "Noise and pollution", pct: 12.5 },
    { label: "Wanting to slow AI expansion", pct: 10.9 },
    { label: "Land use", pct: 7.2 },
    { label: "Construction disruption", pct: 5.8 },
  ],
};

// Reason batteries, as share of reasons cited rather than share of adults:
// these are pick-your-top batteries, so each is normalised by its own total the
// way dataCenterShares.nationalShares does. Asked twice, once about data
// centers nationally and once about one in your own area.
const REASONS = {
  oppose: {
    label: "Why they object",
    tone: "#e15759",
    axisMax: 34,
    note: "The objections barely move between the national and the local question, a maximum shift of 2.3 points on any reason. So this is a stable objection profile rather than a NIMBY reflex: people give the same reasons wherever the data center is. Both leading reasons are material rather than ideological, and wanting to slow AI expansion is only fifth.",
    rows: [
      { label: "Environmental impact (water and land)", nat: 29.8, loc: 30.9 },
      { label: "Push up energy prices",                 nat: 20.6, loc: 20.3 },
      { label: "Noise and light pollution",             nat: 12.5, loc: 13.7 },
      { label: "Distrust the companies building them",  nat: 13.2, loc: 10.9 },
      { label: "Enables AI expansion / job losses",     nat: 10.9, loc: 9.6 },
      { label: "Poor use of land",                      nat: 7.2,  loc: 7.4 },
      { label: "Construction disruption",               nat: 5.8,  loc: 7.3 },
    ],
  },
  support: {
    label: "Why they support",
    tone: "#59a14f",
    axisMax: 24,
    note: "This is where scope changes the argument. The two most abstract reasons, national security and US tech leadership, are the biggest fallers when the question becomes local. The two most tangible, tax revenue and local investment, are the only risers. Nationally people back data centers for the country; locally they need them to pay for something.",
    rows: [
      { label: "Protect national security",             nat: 20.8, loc: 18.0 },
      { label: "Strengthen US tech leadership",         nat: 19.5, loc: 16.3 },
      { label: "Create jobs",                           nat: 18.1, loc: 17.2 },
      { label: "Good for economic growth",              nat: 16.3, loc: 16.0 },
      { label: "Bring investment and infrastructure",   nat: 13.6, loc: 16.2 },
      { label: "Generate tax revenue",                  nat: 11.8, loc: 16.3 },
    ],
  },
};

// Which objection LEADS in each district. Near-uniform, which is the point.
const REASONS_TOPMAP = {
  lead: { label: "Environmental impact", districts: 422 },
  other: { label: "Energy prices", districts: 14, where: "every one of them in the Bay Area and coastal California, led by CA-12, CA-14, CA-16 and CA-17" },
  ofDistricts: 436,
};

// The two spotlights. `id` is the CD area_id, which is what the map highlights
// on, so these rows are clickable.
const DC_SPOTLIGHT = [
  {
    key: "VA", label: "Virginia",
    lead: "The widest national-to-local flip in the country, and the build-out heartland is the exception.",
    body: "Virginia averages a 20.9 point gap between supporting data centers nationally and locally, the largest of any state. But VA-10 and VA-11 in Northern Virginia, where the data centers actually are, are the only two districts still net positive locally. Proximity to existing build-out tracks with support, not opposition.",
    rows: [
      { id: "5110", name: "VA-10", region: "Loudoun / Northern Virginia", nat: 32.9, loc: 4.6 },
      { id: "5111", name: "VA-11", region: "Fairfax",                     nat: 28.6, loc: 4.4 },
      { id: "5107", name: "VA-07", region: "Fredericksburg / Prince William", nat: 17.8, loc: -4.5 },
      { id: "5101", name: "VA-01", region: "Tidewater",                   nat: 12.3, loc: -9.5 },
    ],
    tail: "Most opposed locally: VA-05 at −12.0, VA-09 at −11.6, VA-04 at −10.6.",
  },
  {
    key: "TX", label: "Texas",
    lead: "A much smaller gap, and the widest band of locally positive districts of any large state.",
    body: "Texas averages a 14.1 point gap, well below Virginia's, and its local net ranges from −5.7 to +6.5. It is the most locally receptive large state in the country, which is where the build-out argument is easiest to make rather than hardest.",
    rows: [
      { id: "4803", name: "TX-03", region: "Plano / Collin County", nat: 24.7, loc: 5.1 },
      { id: "4804", name: "TX-04", region: "East Texas",            nat: 21.0, loc: 2.5 },
      { id: "4831", name: "TX-31", region: "Round Rock / Williamson", nat: 18.7, loc: -2.8 },
      { id: "4817", name: "TX-17", region: "Waco / Brazos Valley",  nat: 16.8, loc: -2.7 },
    ],
    tail: "Most opposed locally: TX-27 at −5.7, TX-35 at −5.4, TX-20 at −5.1.",
  },
];

// Wave 4. Keys mirror nji_ad_reaction so the card and the map agree.
const AD_REACTION = {
  rows: [
    { label: "It wouldn't bother me",             pct: 33.3, color: "#59a14f", neg: false },
    { label: "I'd trust that brand somewhat less", pct: 20.6, color: "#f9a099", neg: true },
    { label: "I'd trust that brand much less",     pct: 23.0, color: "#e15759", neg: true },
    { label: "I'd stop buying from that brand",    pct: 9.2,  color: "#C1121F", neg: true },
    { label: "Not sure",                           pct: 13.9, color: "#bab0ab", neg: false },
  ],
  combinedNegative: 52.8,
};

const TAKEAWAYS = [
  {
    title: "Sentiment is rising, and the gap between me and us is not moving",
    desc: "AI for your own life went from net +8.0 to +14.1 across three waves, and AI for society from −9.0 to −3.1. Both improved sharply, yet the distance between them has sat at 17 to 18 points in every single wave. People are getting more optimistic about AI for themselves and staying pessimistic about it for everyone else.",
  },
  {
    title: "The generational divide is now the whole story",
    desc: "On AI for society, under-35s moved from −7.2 to +12.6 while over-65s went from −16.2 to −25.1. The gap between them went from 9 points to 38 in three waves. On AI for your own life, the district distributions of the two groups do not overlap at any point.",
  },
  {
    title: "Support survives until it is nearby",
    desc: "Data centers are net positive nationally in 433 of 436 districts and net negative locally in 390. In 387 districts, 89% of the country, both are true at once. The leading objections are environmental impact and energy prices, not opposition to AI itself.",
  },
];

const NEXT_STEPS = [
  {
    title: "Argue the local case, not the national one",
    desc: "The national argument is already won almost everywhere and the local one is lost almost everywhere. Test siting messages against the two objections that actually lead, environment and energy prices, rather than against AI scepticism.",
  },
  {
    title: "Use proximity as evidence",
    desc: "Northern Virginia, which hosts more data center capacity than anywhere else, is the most locally supportive part of its state. That is a testable argument for communities facing a first build, and it is the opposite of what most siting fights assume.",
  },
  {
    title: "Track the age gap, because it is still widening",
    desc: "Three waves in, the generational divide on AI for society has quadrupled and shows no sign of converging. Re-field the same questions to see whether over-65s keep moving away, because that determines whether time is on the industry's side or not.",
  },
];

const METHOD_CARDS = [
  { title: "The sample", desc: "More than 8,000 US adults surveyed online per wave, balanced to be nationally representative by age, gender, ethnicity, education, party, and region." },
  { title: "The waves", desc: "AI sentiment is tracked across three waves. The usage, advertising and data center questions are all from the most recent round, fielded July 2026." },
  { title: "The method", desc: "Multilevel regression links respondents' characteristics to their answers; poststratification applies those relationships to each geography, giving local estimates for states, counties and congressional districts." },
  { title: "The caveat", desc: "These are modelled estimates rather than measured subgroup counts. The data center questions come from a separate battery fielded at the same time, so they share the moment but not the wave numbering." },
];

// ─── Step definitions ─────────────────────────────────────────────────────────
// Map layers were picked for spread, the same discipline as the prediction
// markets reel. Measured district ranges: AI-for-society net -14.2 to +13.3,
// data-center local net -16.8 to +10.2, rarely-or-never AI use 21.7 to 46.2.
// All three carry real contrast; AI-for-life net is narrower and is only mapped
// where a group filter supplies the variation.
const STEPS = [
  {
    name: "intro",
    headline: "The closer AI gets, the less Americans like it.",
    copy: "Stack has tracked what Americans expect AI to do to their own lives and to society across three waves, and asked in the latest round how they feel about advertising made without a real person in it. The pattern is the same each time: the further away AI stays, the more welcome it is.",
    mapLabel: "AI will improve society, net by district",
    mapView: { variable: "ai_impact_society", geoLevel: "cd", preferenceFilter: "net", viewMode: "heat" },
    flyTo: { type: "fitBounds", bounds: [[-125, 24], [-66, 49]], duration: 0 },
    hero: true,
  },
  {
    name: "trend",
    headline: "Good for me, bad for us. Three waves, same gap.",
    copy: "Expectations for AI improving your own life rose from net +8.0 to +14.1. Expectations for it improving society rose too, from −9.0 to −3.1, but remain negative. What has not shifted is the distance between the two: 17 to 18 points in every wave.",
    mapLabel: "Net expectation by district",
    mapView: { variable: "ai_impact_society", geoLevel: "cd", preferenceFilter: "net", viewMode: "heat" },
    flyTo: { type: "fitBounds", bounds: [[-125, 24], [-66, 49]], duration: 1200 },
    mapHoverHint: true,
  },
  {
    name: "generations",
    headline: "Two generations, no shared ground.",
    copy: "On AI for your own life, the district distributions for under-35s and over-65s do not overlap at any point. On AI for society, under-35s have crossed into positive territory while over-65s have moved further away. Usage explains it: 62% of under-35s use AI daily against 17% of over-65s.",
    mapLabel: "Net expectation by district",
    mapView: { variable: "ai_impact_society", geoLevel: "cd", preferenceFilter: "net", viewMode: "heat" },
    flyTo: { type: "fitBounds", bounds: [[-125, 24], [-66, 49]], duration: 1200 },
  },
  {
    name: "datacenters",
    headline: "Support survives right up until it is nearby.",
    copy: "Data centers are net +9.6 nationally and net −7.1 in people's own areas, a 16.8 point flip. Net positive nationally in 433 of 436 districts, net negative locally in 390, and both at once in 387. The objections that lead are environmental impact and energy prices, not AI itself.",
    mapLabel: "Support for a data center in my area, net by district",
    mapView: { variable: "pol_dc_support_local", geoLevel: "cd", preferenceFilter: "net", viewMode: "heat" },
    flyTo: { type: "fitBounds", bounds: [[-125, 24], [-66, 49]], duration: 1200 },
    mapHoverHint: true,
  },
  {
    name: "reasons",
    headline: "One objection leads almost everywhere, and it is not AI.",
    copy: "Environmental impact is the leading reason to oppose a data center in 422 of 436 districts, followed by energy prices. Wanting to slow AI expansion is only fifth. And the objections barely shift between the national and the local question, so this is a settled set of concerns rather than a reflex about proximity.",
    mapLabel: "Leading local objection by district",
    // Categorical top-reason map. It comes out near-uniform, which is itself
    // the finding, and the handful of exceptions stand out cleanly.
    mapView: { variable: "pol_dc_oppose_reasons_local", geoLevel: "cd", preferenceFilter: "top", viewMode: "top", topStrengthShading: false },
    flyTo: { type: "fitBounds", bounds: [[-125, 24], [-66, 49]], duration: 1200 },
    mapHoverHint: true,
  },
  {
    name: "authenticity",
    headline: "Half the country penalises a brand for using AI in an ad.",
    copy: "Shown an ad they liked and then told the person in it was AI-generated rather than real, 52.8% say they would trust that brand less and 9.2% would stop buying from it altogether. A third would not be bothered. This is the same population that is getting more optimistic about AI in the abstract.",
    mapLabel: "Would trust the brand less, share by district",
    mapView: { variable: "nji_ad_reaction", geoLevel: "cd", preferenceFilter: "negsum", viewMode: "heat" },
    flyTo: { type: "fitBounds", bounds: [[-125, 24], [-66, 49]], duration: 1200 },
    mapHoverHint: true,
  },
  {
    name: "summary",
    headline: "Three things to take with you.",
    copy: "",
    mapLabel: "Support for a data center in my area, net by district",
    mapView: { variable: "pol_dc_support_local", geoLevel: "cd", preferenceFilter: "net", viewMode: "heat" },
    flyTo: { type: "fitBounds", bounds: [[-125, 24], [-66, 49]], duration: 1400 },
    hero: true,
  },
];

const CHAPTER_NAMES = {
  "intro":        "The Pattern",
  "trend":        "Me Versus Us",
  "generations":  "Who Is Optimistic",
  "datacenters":  "When It Is Nearby",
  "reasons":      "What Drives It",
  "authenticity": "The Commercial Cost",
  "summary":      "What To Do With This",
};

// ─── Style tokens ─────────────────────────────────────────────────────────────
const F = "'Inter', 'DM Sans', sans-serif";
const TEAL      = "#0d9488";
const TEAL_DARK = "#0f766e";
const TEAL_GLOW = "rgba(13,148,136,0.18)";
const ACCENT    = "#06b6d4";
const INK       = "#0f172a";
const MUTED     = "#475569";
const MUTED_LIGHT = "#94a3b8";
const PAPER     = "#ffffff";
const PAPER_SOFT = "linear-gradient(160deg, rgba(255,255,255,1) 0%, rgba(248,250,252,0.98) 100%)";
const BORDER    = "rgba(13,148,136,0.1)";
const BG        = "#ffffff";

const FU_KF = {
  "@keyframes fuIn": {
    from: { opacity: 0, transform: "translateY(14px)" },
    to:   { opacity: 1, transform: "translateY(0)" },
  },
};
const fu = (delay) => ({
  animation: `fuIn 0.52s cubic-bezier(0.22,1,0.36,1) ${delay}s both`,
  ...FU_KF,
});
const fuStagger = (base, i) => ({
  animation: `fuIn 0.5s cubic-bezier(0.22,1,0.36,1) ${(base + i * 0.09).toFixed(2)}s both`,
  ...FU_KF,
});

function CountUp({ value, isActive, duration = 900 }) {
  const [display, setDisplay] = useState(value);
  const rafRef = useRef(null);
  useEffect(() => {
    if (!isActive) { setDisplay(value); return; }
    const m = String(value).match(/^([^0-9-]*)(-?\d+\.?\d*)([^0-9]*)$/);
    if (!m) { setDisplay(value); return; }
    const [, prefix, numStr, suffix] = m;
    const target = parseFloat(numStr);
    const hasDecimal = numStr.includes(".");
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      const cur = target * ease;
      setDisplay(`${prefix}${hasDecimal ? cur.toFixed(1) : Math.round(cur)}${suffix}`);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [isActive, value, duration]);
  return <>{display}</>;
}

// ─── Navigation ───────────────────────────────────────────────────────────────
let _cameraTimer = null;
let _lastFlyKey = "";

// A district highlight moves the camera outside navigateToStep's knowledge, so
// its "same flyTo, skip the move" cache has to be invalidated or the next step
// change would leave the map parked on that district.
function invalidateFlyCache() {
  _lastFlyKey = "";
}

function getGlMap(mainInst) {
  return mainInst?._mapPageInstance?.mapRef?.current?.getMap?.() || null;
}

function flyForViewport(fly) {
  return window.innerWidth < 600 ? { ...fly, padding: 0 } : fly;
}

function exitHighlights(navigate) {
  if (window.innerWidth < 600) {
    try { sessionStorage.setItem("m.activeScreen", "map"); } catch (e) { /* ignore */ }
    navigate("/m");
  } else {
    navigate("/");
  }
}

function armCrossfade(glMap, duration = 550) {
  if (!glMap) return;
  let style;
  try { style = glMap.getStyle(); } catch { return; }
  if (!style?.layers) return;
  const t = { duration, delay: 0 };
  style.layers.forEach((layer) => {
    try {
      if (layer.type === "fill") {
        glMap.setPaintProperty(layer.id, "fill-color-transition", t);
        glMap.setPaintProperty(layer.id, "fill-opacity-transition", t);
        glMap.setPaintProperty(layer.id, "fill-outline-color-transition", t);
      } else if (layer.type === "line") {
        glMap.setPaintProperty(layer.id, "line-color-transition", t);
        glMap.setPaintProperty(layer.id, "line-opacity-transition", t);
      }
    } catch { /* safe */ }
  });
}

function navigateToStep(mainInst, stepName, extraMapState = {}) {
  if (!mainInst) return;
  const step = STEPS.find((s) => s.name === stepName);
  if (!step) return;
  if (_cameraTimer) { clearTimeout(_cameraTimer); _cameraTimer = null; }

  mainInst.setState({ chatHighlightAreas: step.highlights || [] });
  armCrossfade(getGlMap(mainInst));

  if (step.mapView) mainInst.setHighlightsState({ ...step.mapView, ...extraMapState });

  if (step.flyTo) {
    const key = JSON.stringify(step.flyTo);
    if (key !== _lastFlyKey) {
      _lastFlyKey = key;
      _cameraTimer = setTimeout(() => {
        _cameraTimer = null;
        mainInst.flyTo(flyForViewport(step.flyTo));
      }, 80);
    }
  }
}

// ─── Shared sub-components ────────────────────────────────────────────────────
function StackLogo() {
  return (
    <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
      <img src="/static/images/logo.png" alt="District Profiles" style={{ height: 22, width: "auto", display: "block" }} />
    </Box>
  );
}

function Eyebrow({ children }) {
  return (
    <Typography sx={{ fontSize: "10px", fontWeight: 800, letterSpacing: "0.16em", textTransform: "uppercase", color: TEAL, fontFamily: F, mb: 1.4 }}>
      {children}
    </Typography>
  );
}

function Headline({ children }) {
  return (
    <Typography sx={{ fontSize: { xs: "20px", sm: "25px", md: "29px" }, fontWeight: 800, lineHeight: { xs: 1.22, md: 1.16 }, letterSpacing: "-0.03em", color: INK, fontFamily: F, mb: 1.6 }}>
      {children}
    </Typography>
  );
}

function BodyCopy({ children }) {
  return (
    <Typography sx={{ fontSize: { xs: "14px", md: "15px" }, color: MUTED, lineHeight: 1.8, fontFamily: F, whiteSpace: "pre-line", letterSpacing: "-0.01em" }}>
      {children}
    </Typography>
  );
}

function Panel({ children, sx = {}, ...props }) {
  return (
    <Box {...props} sx={{ width: "100%", p: { xs: 2, md: 2.5 }, borderRadius: "22px", bgcolor: PAPER, backgroundImage: PAPER_SOFT, border: `1px solid ${BORDER}`, boxShadow: "0 8px 28px rgba(13,148,136,0.06)", ...sx }}>
      {children}
    </Box>
  );
}

function CardLabel({ children }) {
  return (
    <Typography sx={{ fontSize: "9px", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED_LIGHT, fontFamily: F, mb: 1 }}>
      {children}
    </Typography>
  );
}

function Note({ children }) {
  return (
    <Box sx={{ mt: 1.25, px: 1.5, py: 1.1, borderRadius: "12px", bgcolor: `${TEAL}06`, border: `1px solid ${TEAL}18` }}>
      <Typography sx={{ fontSize: "11.5px", color: MUTED, fontFamily: F, lineHeight: 1.65 }}>{children}</Typography>
    </Box>
  );
}

function StepHeader({ index, total, chapter, isActive, onScrollNext, hasNext }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: "auto", pt: 3.5, opacity: isActive ? 1 : 0, transition: "opacity 0.35s ease", minHeight: 28 }}>
      <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: TEAL, fontFamily: F }}>
        {index + 1} of {total}
      </Typography>
      {chapter && (
        <Typography sx={{ fontSize: "10px", color: MUTED_LIGHT, fontFamily: F, letterSpacing: "0.02em" }}>· {chapter}</Typography>
      )}
      {hasNext && (
        <Box onClick={onScrollNext} sx={{ ml: "auto", display: "inline-flex", alignItems: "center", gap: 0.75, cursor: "pointer", px: 1.4, py: 0.75, borderRadius: "10px", border: `1px solid ${TEAL}30`, bgcolor: `${TEAL}08`, transition: "all 0.18s ease", "&:hover": { bgcolor: `${TEAL}14`, borderColor: `${TEAL}55` } }}>
          <Typography sx={{ fontSize: "11px", fontWeight: 700, color: TEAL, fontFamily: F, lineHeight: 1 }}>Scroll</Typography>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
        </Box>
      )}
    </Box>
  );
}

function MapHint({ text }) {
  return (
    <Box sx={{
      position: "absolute", bottom: 20, left: "50%", transform: "translateX(-50%)",
      zIndex: 12, display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 0.8,
      px: 1.5, py: 0.75, borderRadius: "999px",
      bgcolor: "rgba(15,23,42,0.72)", backdropFilter: "blur(8px)",
      boxShadow: "0 4px 18px rgba(0,0,0,0.18)", whiteSpace: "nowrap", pointerEvents: "none",
      animation: "hintEntrance 0.5s cubic-bezier(0.22,1,0.36,1) 0.8s both",
      "@keyframes hintEntrance": { from: { opacity: 0, transform: "translateX(-50%) translateY(8px)" }, to: { opacity: 1, transform: "translateX(-50%) translateY(0)" } },
    }}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
      </svg>
      <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,0.88)", fontFamily: F, fontWeight: 600, lineHeight: 1 }}>
        {text || "Hover over any district for its local numbers"}
      </Typography>
    </Box>
  );
}

// One distribution strip: the 436 district estimates for a group, as a box and
// whiskers on a shared axis. Same treatment as the prediction-markets reel,
// because it is the only encoding that compares groups AND shows the spread
// inside each without a colour ramp having to do both jobs.
// Positive and negative tones. On a NET measure the sign is the finding, so the
// strips are coloured by it rather than by which question is selected: a
// district-median of -5.8 has to read as negative at a glance, not as a paler
// shade of the same green as +27.7.
const NET_POS = "#59a14f";
const NET_NEG = "#e15759";
const toneFor = (v) => (v >= 0 ? NET_POS : NET_NEG);

function DistStrip({ row, axis, selected, onClick, showAxis }) {
  const [lo, hi] = axis;
  const pos = (v) => ((v - lo) / (hi - lo)) * 100;
  const [dmin, q1, med, q3, dmax] = row.jul;
  const shift = Math.round((med - row.w3Med) * 10) / 10;
  const zero = pos(0);
  const tone = toneFor(med);

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex", alignItems: "center", gap: 1.25, cursor: onClick ? "pointer" : "default",
        px: 0.85, py: 0.75, borderRadius: "9px", mb: 0.4,
        // Selection is a hairline, not a fill. A tinted row behind a tinted bar
        // was reading as a grey block sitting under the data.
        bgcolor: "transparent",
        border: `1px solid ${selected ? tone + "55" : "transparent"}`,
        transition: "all 0.15s ease",
        ...(onClick ? { "&:hover": { borderColor: selected ? tone + "77" : "rgba(15,23,42,0.12)" } } : {}),
      }}
    >
      <Typography sx={{ fontSize: "11.5px", fontWeight: selected || row.ref ? 800 : 600, color: selected || row.ref ? INK : MUTED, fontFamily: F, minWidth: 84, lineHeight: 1.2 }}>
        {row.name}
      </Typography>

      <Box sx={{ flex: 1, position: "relative", height: 24 }}>
        {/* Zero line only. The full-width neutral track that used to sit here
            read as a grey shadow behind every row and carried no information. */}
        <Box sx={{ position: "absolute", top: 1, bottom: 5, left: `${zero}%`, width: "1px", bgcolor: "rgba(15,23,42,0.25)" }} />
        {/* Lowest to highest district */}
        <Box sx={{ position: "absolute", top: 11, height: 2, left: `${pos(dmin)}%`, width: `${pos(dmax) - pos(dmin)}%`, bgcolor: tone, opacity: 0.45, borderRadius: 1 }} />
        {/* Middle half */}
        <Box sx={{ position: "absolute", top: 6, height: 12, left: `${pos(q1)}%`, width: `${pos(q3) - pos(q1)}%`, bgcolor: tone, opacity: 0.55, borderRadius: "3px" }} />
        {/* Previous wave's median, hollow */}
        <Box sx={{ position: "absolute", top: 4, height: 16, width: 2, left: `${pos(row.w3Med)}%`, bgcolor: "#fff", border: `1px solid ${INK}55`, borderRadius: 1 }} />
        {/* Latest median, solid. No drop shadow: it sat over the bar as a grey halo. */}
        <Box sx={{ position: "absolute", top: 2, height: 20, width: 3, left: `${pos(med)}%`, bgcolor: tone, borderRadius: 1 }} />
        {showAxis && [lo, 0, hi].map((v) => (
          <Typography key={v} sx={{ position: "absolute", top: 24, left: `${pos(v)}%`, transform: "translateX(-50%)", fontSize: "8.5px", color: MUTED_LIGHT, fontFamily: F }}>
            {v > 0 ? `+${v}` : v}
          </Typography>
        ))}
      </Box>

      <Typography sx={{ fontSize: "12px", fontWeight: 700, color: tone, fontFamily: F, minWidth: 46, textAlign: "right" }}>
        {med > 0 ? "+" : ""}{med.toFixed(1)}
      </Typography>
      <Typography sx={{ fontSize: "11px", fontWeight: 700, fontFamily: F, minWidth: 38, textAlign: "right", color: shift > 0 ? "#059669" : shift < 0 ? "#e11d48" : MUTED_LIGHT }}>
        {shift > 0 ? "+" : ""}{shift.toFixed(1)}
      </Typography>
    </Box>
  );
}

// ─── Cards ────────────────────────────────────────────────────────────────────
function IntroCard({ step, onScrollNext }) {
  return (
    <>
      <Box sx={fu(0.05)}><StackLogo /></Box>
      <Box sx={{ ...fu(0.1), maxWidth: 900, mx: "auto" }}>
        <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.22em", textTransform: "uppercase", color: TEAL, fontFamily: F, mb: 1.3 }}>
          AI &amp; Data Centers
        </Typography>
        <Typography sx={{ fontSize: { xs: "29px", sm: "37px", md: "44px" }, fontWeight: 500, lineHeight: { xs: 1.12, md: 1.08 }, letterSpacing: "-0.035em", color: INK, fontFamily: F, mb: 1.5 }}>
          {step.headline}
        </Typography>
        <Typography sx={{ fontSize: { xs: "15px", md: "19px" }, fontWeight: 400, color: "rgba(15,23,42,0.66)", fontFamily: F, lineHeight: 1.4, maxWidth: 820, mx: "auto" }}>
          {step.copy}
        </Typography>
      </Box>

      {/* The spine as four numbers, which is the whole reel in one glance. */}
      <Box sx={{ ...fu(0.26), mt: 2.4, width: "100%" }}>
        <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(15,23,42,0.38)", fontFamily: F, mb: 1.15 }}>
          The same question, at four distances
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1.1 }}>
          {PROXIMITY.map((p, i) => (
            <Box key={p.label} sx={{ textAlign: "left", px: 1.5, pt: 1.4, pb: 1.3, borderRadius: "16px", border: `1px solid ${BORDER}`, bgcolor: "rgba(255,255,255,0.82)", boxShadow: "0 8px 24px rgba(15,23,42,0.04)", ...fuStagger(0.3, i) }}>
              <Typography sx={{ fontSize: "26px", fontWeight: 800, color: p.tone, fontFamily: F, lineHeight: 1, letterSpacing: "-0.04em" }}>{p.value}</Typography>
              <Typography sx={{ fontSize: "11.5px", fontWeight: 700, color: INK, fontFamily: F, lineHeight: 1.35, mt: 0.6 }}>{p.label}</Typography>
              <Typography sx={{ fontSize: "9.5px", color: MUTED_LIGHT, fontFamily: F, lineHeight: 1.35, mt: 0.3 }}>{p.note}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ ...fu(0.38), mt: 2, width: "100%" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1.1 }}>
          {METHOD_CARDS.map((c, i) => (
            <Box key={c.title} sx={{ textAlign: "left", px: 1.4, pt: 1.3, pb: 1.2, borderRadius: "14px", border: `1px solid ${TEAL}2a`, bgcolor: `${TEAL}07` }}>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.7, mb: 0.5 }}>
                <Typography sx={{ fontSize: "10px", fontWeight: 800, color: TEAL, fontFamily: F }}>{`0${i + 1}`}</Typography>
                <Typography sx={{ fontSize: "11px", fontWeight: 700, color: INK, fontFamily: F }}>{c.title}</Typography>
              </Box>
              <Typography sx={{ fontSize: "11.5px", color: "rgba(15,23,42,0.6)", fontFamily: F, lineHeight: 1.45 }}>{c.desc}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box onClick={onScrollNext} sx={{ ...fu(0.46), mt: 2.4, display: { xs: "none", sm: "flex" }, alignItems: "center", justifyContent: "center", gap: 1, cursor: "pointer", userSelect: "none", "&:hover .lbl": { color: "rgba(15,23,42,0.68)" } }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: "50%", border: `1px solid ${TEAL}44`, bgcolor: "rgba(255,255,255,0.7)", animation: "scrollBounce 1.6s ease-in-out 1.5s infinite", "@keyframes scrollBounce": { "0%,100%": { transform: "translateY(0)" }, "50%": { transform: "translateY(5px)" } } }}>
          <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M2 3.5L5 6.5L8 3.5" stroke={TEAL} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </Box>
        <Typography className="lbl" sx={{ fontSize: "11px", fontWeight: 600, color: "rgba(15,23,42,0.38)", fontFamily: F, letterSpacing: "0.1em", textTransform: "uppercase", transition: "color 0.2s ease" }}>
          Scroll for the first finding
        </Typography>
      </Box>
    </>
  );
}

// Slide 2. One idea: both measures rose, the gap between them did not move.
function TrendCard({ step, isActive, measure, onMeasureChange }) {
  const m = AI_TREND[measure];
  const waves = [2, 3, 4];

  return (
    <>
      <StackLogo />
      <Eyebrow>Three waves · AI expectations</Eyebrow>
      <Headline>{step.headline}</Headline>
      <BodyCopy>{step.copy}</BodyCopy>

      <Box sx={{ mt: 1.75, width: "100%", borderRadius: "16px", border: `1px solid ${BORDER}`, overflow: "hidden", bgcolor: PAPER, backgroundImage: PAPER_SOFT, boxShadow: "0 8px 28px rgba(13,148,136,0.06)" }}>
        <Box sx={{ display: "flex", borderBottom: `1px solid ${BORDER}` }}>
          {["life", "society"].map((k) => {
            const on = k === measure;
            return (
              <Box key={k} onClick={() => onMeasureChange(k)} sx={{ flex: 1, py: 1, cursor: "pointer", textAlign: "center", bgcolor: on ? `${TEAL}10` : "transparent", borderBottom: on ? `2.5px solid ${TEAL}` : "2.5px solid transparent", transition: "all 0.15s ease", "&:hover": { bgcolor: `${TEAL}0c` } }}>
                <Typography sx={{ fontSize: "11px", fontWeight: on ? 700 : 500, color: on ? TEAL : MUTED_LIGHT, fontFamily: F }}>
                  {AI_TREND[k].label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Box key={measure} sx={{ p: 2.25, animation: "fuIn 0.25s ease both", ...FU_KF }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.75 }}>
            <Typography sx={{ fontSize: "46px", fontWeight: 800, fontFamily: F, lineHeight: 1, letterSpacing: "-0.05em", color: m.color }}>
              <CountUp value={`${m.nets[4] > 0 ? "+" : ""}${m.nets[4].toFixed(1)}`} isActive={isActive} />
            </Typography>
            <Typography sx={{ fontSize: "12.5px", color: MUTED, fontFamily: F, lineHeight: 1.6, flex: 1 }}>
              net in the latest wave for <strong style={{ color: INK }}>{m.label.toLowerCase()}</strong>, from {m.nets[2] > 0 ? "+" : ""}{m.nets[2].toFixed(1)} two waves earlier.
            </Typography>
          </Box>

          <CardLabel>Full breakdown by wave</CardLabel>
          <TrendLegend />

          {/* Column header, on the same grid as the rows below, so the number on
              the right is labelled rather than left to be inferred. */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "48px 1fr 46px", sm: "54px 1fr 52px" }, alignItems: "center", gap: 1, mb: 0.5 }}>
            <Box />
            <Box />
            <Typography sx={{ fontSize: "8.5px", fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: MUTED_LIGHT, fontFamily: F, textAlign: "right" }}>
              Net
            </Typography>
          </Box>

          {waves.map((w) => (
            <Box key={w} sx={{ display: "grid", gridTemplateColumns: { xs: "48px 1fr 46px", sm: "54px 1fr 52px" }, alignItems: "center", gap: 1, mb: 0.7 }}>
              <Typography sx={{ fontSize: "10.5px", fontWeight: 700, color: MUTED, fontFamily: F }}>{WAVE_LABEL[w]}</Typography>
              {/* Each share sits INSIDE its own segment, so the number and the
                  colour it belongs to cannot be mismatched by the reader. The
                  bar is tall enough to carry text, which the old 10px one was
                  not. A segment under 7% is left blank rather than overflowing. */}
              <Box sx={{ display: "flex", height: 20, borderRadius: "5px", overflow: "hidden" }}>
                {TREND_SEGMENTS.map((seg, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: `${m.shares[w][i]}%`, bgcolor: seg.color,
                      display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
                    }}
                  >
                    {m.shares[w][i] >= 7 && (
                      <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: seg.ink, fontFamily: F, lineHeight: 1 }}>
                        {m.shares[w][i].toFixed(1)}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
              <Typography sx={{ fontSize: "13px", fontWeight: 800, color: m.nets[w] > 0 ? "#059669" : "#e11d48", fontFamily: F, textAlign: "right", letterSpacing: "-0.02em" }}>
                {m.nets[w] > 0 ? "+" : "−"}{Math.abs(m.nets[w]).toFixed(1)}
              </Typography>
            </Box>
          ))}

          {/* What the net actually is. It is "mostly better" minus "mostly
              worse", which is not guessable from a bar with four segments in
              it, and the don't-knows sit in the bar without being in the net. */}
          <Typography sx={{ mt: 1.1, fontSize: { xs: "10.5px", sm: "9.5px" }, color: MUTED_LIGHT, fontFamily: F, lineHeight: 1.5 }}>
            Net is “mostly better” minus “mostly worse”. Don’t-knows are shown in the bar but left out of it.
          </Typography>
        </Box>
      </Box>
    </>
  );
}

// Slide 3. One idea: the two ends of the age range have no shared ground.
function GenerationsCard({ step, measure, onMeasureChange, rowIdx, onRowChange }) {
  const dist = AI_DIST[measure];
  const selectable = dist.rows.filter((r) => !r.ref);

  return (
    <>
      <StackLogo />
      <Eyebrow>436 districts · by age</Eyebrow>
      <Headline>{step.headline}</Headline>
      <BodyCopy>{step.copy}</BodyCopy>

      <Box sx={{ mt: 1.75, width: "100%", borderRadius: "16px", border: `1px solid ${BORDER}`, overflow: "hidden", bgcolor: PAPER, backgroundImage: PAPER_SOFT, boxShadow: "0 8px 28px rgba(13,148,136,0.06)" }}>
        <Box sx={{ display: "flex", borderBottom: `1px solid ${BORDER}` }}>
          {["life", "society"].map((k) => {
            const on = k === measure;
            return (
              <Box key={k} onClick={() => onMeasureChange(k)} sx={{ flex: 1, py: 1, cursor: "pointer", textAlign: "center", bgcolor: on ? `${TEAL}10` : "transparent", borderBottom: on ? `2.5px solid ${TEAL}` : "2.5px solid transparent", transition: "all 0.15s ease", "&:hover": { bgcolor: `${TEAL}0c` } }}>
                <Typography sx={{ fontSize: "11px", fontWeight: on ? 700 : 500, color: on ? TEAL : MUTED_LIGHT, fontFamily: F }}>{AI_TREND[k].label}</Typography>
              </Box>
            );
          })}
        </Box>

        <Box key={measure} sx={{ p: 2.25, animation: "fuIn 0.25s ease both", ...FU_KF }}>
          <CardLabel>Net expectation by district, shared axis · tap a group to remap</CardLabel>

          <DistStrip row={dist.rows[0]} axis={dist.axis} />
          <Box sx={{ height: "1px", bgcolor: BORDER, my: 0.7 }} />

          {selectable.map((r, i) => (
            <DistStrip
              key={r.name}
              row={r}
              axis={dist.axis}
              selected={i === rowIdx}
              onClick={() => onRowChange(i)}
              showAxis={i === selectable.length - 1}
            />
          ))}

          <Box sx={{ mt: 2.2, display: "flex", alignItems: "center", gap: 1.75, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 16, height: 10, borderRadius: "3px", bgcolor: NET_POS, opacity: 0.55 }} />
              <Box sx={{ width: 16, height: 10, borderRadius: "3px", bgcolor: NET_NEG, opacity: 0.55 }} />
              <Typography sx={{ fontSize: "10px", color: MUTED_LIGHT, fontFamily: F, ml: 0.2 }}>Middle half, green net positive and red net negative</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
              <Box sx={{ width: 3, height: 14, borderRadius: 1, bgcolor: INK, opacity: 0.7 }} />
              <Typography sx={{ fontSize: "10px", color: MUTED_LIGHT, fontFamily: F }}>Latest median</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
              <Box sx={{ width: 2, height: 14, borderRadius: 1, bgcolor: "#fff", border: `1px solid ${INK}55` }} />
              <Typography sx={{ fontSize: "10px", color: MUTED_LIGHT, fontFamily: F }}>Previous wave</Typography>
            </Box>
          </Box>

          <Note>{dist.note}</Note>

          <Box sx={{ mt: 1.5, pt: 1.4, borderTop: `1px solid ${BORDER}` }}>
            <CardLabel>And the mechanism is usage, not opinion</CardLabel>
            {AI_USE.byAge.map((r) => (
              <Box key={r.label} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Typography sx={{ fontSize: "11.5px", color: MUTED, fontFamily: F, minWidth: 52 }}>{r.label}</Typography>
                <Box sx={{ flex: 1, height: 7, borderRadius: "3px", bgcolor: "rgba(46,125,50,0.14)", overflow: "hidden" }}>
                  <Box sx={{ width: `${r.daily}%`, height: "100%", bgcolor: "#2E7D32" }} />
                </Box>
                <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#2E7D32", fontFamily: F, minWidth: 44, textAlign: "right" }}>{r.daily}%</Typography>
                <Typography sx={{ fontSize: "10px", color: MUTED_LIGHT, fontFamily: F, minWidth: 96, textAlign: "right" }}>{r.rarely}% rarely/never</Typography>
              </Box>
            ))}
            <Typography sx={{ fontSize: "10px", color: MUTED_LIGHT, fontFamily: F, mt: 0.6, fontStyle: "italic", lineHeight: 1.5 }}>
              Bar is daily use. Nationally 40.2% use AI daily and 24.0% weekly, so 64% use it weekly or more.
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
}

// Slide 4. One idea: the same people support this nationally and refuse it locally.
function DataCentersCard({ step, isActive, spotIdx, onSpotChange, highlightId, onHighlight }) {
  const spot = DC_SPOTLIGHT[spotIdx];

  const bar = (label, d, tone) => (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 0.35 }}>
        <Typography sx={{ fontSize: "11px", fontWeight: 700, color: INK, fontFamily: F, minWidth: 104 }}>{label}</Typography>
        <Typography sx={{ fontSize: "10px", color: MUTED_LIGHT, fontFamily: F, flex: 1 }}>
          support {d.support.toFixed(1)} · neither {d.neither.toFixed(1)} · oppose {d.oppose.toFixed(1)}
        </Typography>
        <Typography sx={{ fontSize: "12.5px", fontWeight: 800, color: tone, fontFamily: F }}>
          {d.net > 0 ? "+" : ""}{d.net.toFixed(1)}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", height: 10, borderRadius: "5px", overflow: "hidden" }}>
        <Box sx={{ width: `${d.support}%`, bgcolor: "#59a14f" }} />
        <Box sx={{ width: `${d.neither}%`, bgcolor: "#bab0ab" }} />
        <Box sx={{ width: `${d.oppose}%`, bgcolor: "#e15759" }} />
      </Box>
    </Box>
  );

  return (
    <>
      <StackLogo />
      <Eyebrow>Data centers · latest wave</Eyebrow>
      <Headline>{step.headline}</Headline>
      <BodyCopy>{step.copy}</BodyCopy>

      <Box sx={{ mt: 1.75, width: "100%", borderRadius: "16px", border: `1px solid ${BORDER}`, overflow: "hidden", bgcolor: PAPER, backgroundImage: PAPER_SOFT, boxShadow: "0 8px 28px rgba(13,148,136,0.06)" }}>
        <Box sx={{ p: 2.25 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.75 }}>
            <Typography sx={{ fontSize: "46px", fontWeight: 800, fontFamily: F, lineHeight: 1, letterSpacing: "-0.05em", color: "#e15759" }}>
              <CountUp value={`${DC.bothCount}`} isActive={isActive} />
            </Typography>
            <Typography sx={{ fontSize: "12.5px", color: MUTED, fontFamily: F, lineHeight: 1.6, flex: 1 }}>
              of {DC.ofDistricts} districts are net positive on data centers nationally and net negative on one in their own area. Both true at once, in 89% of the country.
            </Typography>
          </Box>

          {bar("Nationally", DC.national, "#059669")}
          {bar("In my area", DC.local, "#e11d48")}
          <Typography sx={{ fontSize: "10px", color: MUTED_LIGHT, fontFamily: F, mt: 0.2, fontStyle: "italic" }}>
            Net positive nationally in {DC.posNationally} of {DC.ofDistricts} districts, net negative locally in {DC.negLocally}.
          </Typography>

          <Box sx={{ mt: 1.6, pt: 1.4, borderTop: `1px solid ${BORDER}` }}>
            <Box sx={{ display: "flex", gap: 0.6, mb: 1.1 }}>
              {DC_SPOTLIGHT.map((s, i) => {
                const on = i === spotIdx;
                return (
                  <Box key={s.key} onClick={() => onSpotChange(i)} sx={{ px: 1.4, py: 0.6, borderRadius: "999px", cursor: "pointer", bgcolor: on ? TEAL : "transparent", border: `1.5px solid ${on ? TEAL : TEAL + "55"}`, color: on ? "#fff" : MUTED, fontSize: "11px", fontWeight: 700, fontFamily: F, transition: "all 0.18s ease", userSelect: "none", "&:hover": { bgcolor: on ? TEAL_DARK : `${TEAL}18` } }}>
                    {s.label}
                  </Box>
                );
              })}
            </Box>
            <Typography sx={{ fontSize: "12px", fontWeight: 700, color: INK, fontFamily: F, lineHeight: 1.45, mb: 0.5 }}>{spot.lead}</Typography>
            <Typography sx={{ fontSize: "11.5px", color: MUTED, fontFamily: F, lineHeight: 1.65, mb: 1 }}>{spot.body}</Typography>

            <CardLabel>Tap a district to outline it on the map</CardLabel>
            {spot.rows.map((d) => {
              const on = highlightId === d.id;
              return (
                <Box
                  key={d.id}
                  onClick={() => onHighlight(on ? null : d.id)}
                  sx={{
                    display: "flex", alignItems: "center", gap: 0.9, cursor: "pointer",
                    px: 0.7, py: 0.45, mb: 0.3, borderRadius: "7px",
                    bgcolor: on ? `${TEAL}14` : "transparent",
                    border: `1px solid ${on ? TEAL + "44" : "transparent"}`,
                    transition: "all 0.15s ease",
                    "&:hover": { bgcolor: on ? `${TEAL}18` : "rgba(15,23,42,0.03)" },
                  }}
                >
                  <Typography sx={{ fontSize: "11px", fontWeight: 800, color: INK, fontFamily: F, minWidth: 44, flexShrink: 0 }}>{d.name}</Typography>
                  <Typography sx={{ fontSize: "9.5px", color: MUTED_LIGHT, fontFamily: F, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.region}</Typography>
                  <Typography sx={{ fontSize: "10px", color: MUTED_LIGHT, fontFamily: F, flexShrink: 0 }}>nat {d.nat > 0 ? "+" : ""}{d.nat.toFixed(1)}</Typography>
                  <Typography sx={{ fontSize: "11px", fontWeight: 700, color: d.loc > 0 ? "#059669" : "#e11d48", fontFamily: F, minWidth: 52, textAlign: "right", flexShrink: 0 }}>
                    local {d.loc > 0 ? "+" : ""}{d.loc.toFixed(1)}
                  </Typography>
                </Box>
              );
            })}
            <Typography sx={{ fontSize: "10px", color: MUTED_LIGHT, fontFamily: F, mt: 0.5, fontStyle: "italic", lineHeight: 1.5 }}>{spot.tail}</Typography>
            {highlightId && (
              <Box onClick={() => onHighlight(null)} sx={{ mt: 0.8, display: "inline-flex", alignItems: "center", cursor: "pointer", px: 1.1, py: 0.45, borderRadius: "999px", border: `1px solid ${TEAL}44`, bgcolor: `${TEAL}08`, "&:hover": { bgcolor: `${TEAL}14` } }}>
                <Typography sx={{ fontSize: "10px", fontWeight: 700, color: TEAL, fontFamily: F }}>Clear outline</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}

// Slide 5. One idea: what actually drives the local view. Paired bars for the
// national and local versions of the same battery, so the SHIFT is the thing
// you read rather than either column on its own.
function ReasonsCard({ step, isActive, side, onSideChange }) {
  const r = REASONS[side];
  const max = r.axisMax;

  return (
    <>
      <StackLogo />
      <Eyebrow>Data centers · reasons given</Eyebrow>
      <Headline>{step.headline}</Headline>
      <BodyCopy>{step.copy}</BodyCopy>

      <Box sx={{ mt: 1.75, width: "100%", borderRadius: "16px", border: `1px solid ${BORDER}`, overflow: "hidden", bgcolor: PAPER, backgroundImage: PAPER_SOFT, boxShadow: "0 8px 28px rgba(13,148,136,0.06)" }}>
        <Box sx={{ display: "flex", borderBottom: `1px solid ${BORDER}` }}>
          {["oppose", "support"].map((k) => {
            const on = k === side;
            return (
              <Box key={k} onClick={() => onSideChange(k)} sx={{ flex: 1, py: 1, cursor: "pointer", textAlign: "center", bgcolor: on ? `${REASONS[k].tone}12` : "transparent", borderBottom: on ? `2.5px solid ${REASONS[k].tone}` : "2.5px solid transparent", transition: "all 0.15s ease", "&:hover": { bgcolor: `${REASONS[k].tone}0b` } }}>
                <Typography sx={{ fontSize: "11px", fontWeight: on ? 700 : 500, color: on ? REASONS[k].tone : MUTED_LIGHT, fontFamily: F }}>
                  {REASONS[k].label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Box key={side} sx={{ p: 2.25, animation: "fuIn 0.25s ease both", ...FU_KF }}>
          {side === "oppose" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.75 }}>
              <Typography sx={{ fontSize: "46px", fontWeight: 800, fontFamily: F, lineHeight: 1, letterSpacing: "-0.05em", color: r.tone }}>
                <CountUp value={`${REASONS_TOPMAP.lead.districts}`} isActive={isActive} />
              </Typography>
              <Typography sx={{ fontSize: "12.5px", color: MUTED, fontFamily: F, lineHeight: 1.6, flex: 1 }}>
                of {REASONS_TOPMAP.ofDistricts} districts put <strong style={{ color: INK }}>environmental impact</strong> first. Energy prices lead in the other {REASONS_TOPMAP.other.districts}.
              </Typography>
            </Box>
          )}

          <CardLabel>Share of reasons cited · asked about the country, then about your own area</CardLabel>
          {r.rows.map((row) => {
            const shift = Math.round((row.loc - row.nat) * 10) / 10;
            return (
              <Box key={row.label} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                <Typography sx={{ fontSize: "11.5px", color: MUTED, fontFamily: F, minWidth: 218, lineHeight: 1.3 }}>{row.label}</Typography>
                {/* Two stacked hairline bars: national above, local below, so a
                    difference in length is the whole read. */}
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.4 }}>
                  <Box sx={{ height: 7, borderRadius: "3px", bgcolor: `${r.tone}14`, overflow: "hidden" }}>
                    <Box sx={{ width: `${(row.nat / max) * 100}%`, height: "100%", bgcolor: r.tone, opacity: 0.45, borderRadius: "3px" }} />
                  </Box>
                  <Box sx={{ height: 7, borderRadius: "3px", bgcolor: `${r.tone}14`, overflow: "hidden" }}>
                    <Box sx={{ width: `${(row.loc / max) * 100}%`, height: "100%", bgcolor: r.tone, borderRadius: "3px" }} />
                  </Box>
                </Box>
                <Typography sx={{ fontSize: "10.5px", color: MUTED_LIGHT, fontFamily: F, minWidth: 66, textAlign: "right" }}>
                  {row.nat.toFixed(1)} → {row.loc.toFixed(1)}
                </Typography>
                <Typography sx={{ fontSize: "11px", fontWeight: 700, fontFamily: F, minWidth: 38, textAlign: "right", color: shift > 0 ? "#059669" : shift < 0 ? "#e11d48" : MUTED_LIGHT }}>
                  {shift > 0 ? "+" : ""}{shift.toFixed(1)}
                </Typography>
              </Box>
            );
          })}
          <Typography sx={{ fontSize: "10px", color: MUTED_LIGHT, fontFamily: F, mt: 0.3, fontStyle: "italic", lineHeight: 1.5 }}>
            Pale bar is the national question, solid bar is your own area. Right-hand figure is the shift between them.
          </Typography>

          <Note>{r.note}</Note>

          {side === "oppose" && (
            <Typography sx={{ fontSize: "10.5px", color: MUTED_LIGHT, fontFamily: F, mt: 1, lineHeight: 1.55 }}>
              The {REASONS_TOPMAP.other.districts} districts where energy prices lead instead are {REASONS_TOPMAP.other.where}.
            </Typography>
          )}
        </Box>
      </Box>
    </>
  );
}

// Slide 6. One idea: AI in an ad costs a brand trust.
function AuthenticityCard({ step, isActive }) {
  return (
    <>
      <StackLogo />
      <Eyebrow>Authenticity · latest wave</Eyebrow>
      <Headline>{step.headline}</Headline>
      <BodyCopy>{step.copy}</BodyCopy>

      <Box sx={{ mt: 1.75, width: "100%", borderRadius: "16px", border: `1px solid ${BORDER}`, overflow: "hidden", bgcolor: PAPER, backgroundImage: PAPER_SOFT, boxShadow: "0 8px 28px rgba(13,148,136,0.06)" }}>
        <Box sx={{ p: 2.25 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.75 }}>
            <Typography sx={{ fontSize: "46px", fontWeight: 800, fontFamily: F, lineHeight: 1, letterSpacing: "-0.05em", color: "#C1121F" }}>
              <CountUp value={`${AD_REACTION.combinedNegative}%`} isActive={isActive} />
            </Typography>
            <Typography sx={{ fontSize: "12.5px", color: MUTED, fontFamily: F, lineHeight: 1.6, flex: 1 }}>
              would trust the brand less after learning the person in an ad they liked was AI-generated. A third would not be bothered.
            </Typography>
          </Box>

          <CardLabel>Reaction to an AI-generated ad</CardLabel>
          <Box sx={{ display: "flex", height: 12, borderRadius: "6px", overflow: "hidden", mb: 1.4 }}>
            {AD_REACTION.rows.map((r) => (
              <Box key={r.label} sx={{ width: `${r.pct}%`, bgcolor: r.color }} />
            ))}
          </Box>
          {AD_REACTION.rows.map((r) => (
            <Box key={r.label} sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.55 }}>
              <Box sx={{ width: 9, height: 9, borderRadius: "2px", bgcolor: r.color, flexShrink: 0 }} />
              <Typography sx={{ fontSize: "12px", color: r.neg ? INK : MUTED, fontWeight: r.neg ? 600 : 400, fontFamily: F, minWidth: 232, lineHeight: 1.3 }}>{r.label}</Typography>
              <Box sx={{ flex: 1, height: 6, borderRadius: "3px", bgcolor: `${r.color}26`, overflow: "hidden" }}>
                <Box sx={{ width: `${(r.pct / 36) * 100}%`, height: "100%", bgcolor: r.color, borderRadius: "3px" }} />
              </Box>
              <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: r.color, fontFamily: F, minWidth: 42, textAlign: "right" }}>{r.pct}%</Typography>
            </Box>
          ))}

          <Note>
            This is the same public that is getting steadily more optimistic about AI in the abstract. Optimism about the technology and tolerance for it in your own marketing are different measures, and the second one is a cost.
          </Note>
        </Box>
      </Box>
    </>
  );
}

function SummaryCard({ step, authState, onExit }) {
  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", width: "100%" }}>
        <StackLogo />
        <Eyebrow>What To Do With This</Eyebrow>
        <Typography sx={{ fontSize: { xs: "28px", sm: "34px" }, fontWeight: 500, color: INK, fontFamily: F, lineHeight: 1.1, letterSpacing: "-0.03em", mb: 2 }}>
          {step.headline}
        </Typography>
      </Box>

      <Box sx={{ display: "grid", gap: 1.1, width: "100%" }}>
        {TAKEAWAYS.map((t, i) => (
          <Box key={t.title} sx={{ px: 1.7, py: 1.4, borderRadius: "16px", border: `1px solid ${BORDER}`, bgcolor: "rgba(255,255,255,0.9)", textAlign: "left" }}>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.8, mb: 0.4 }}>
              <Typography sx={{ fontSize: "11px", fontWeight: 800, color: TEAL, fontFamily: F }}>{`0${i + 1}`}</Typography>
              <Typography sx={{ fontSize: { xs: "14.5px", sm: "13px" }, fontWeight: 700, color: INK, fontFamily: F }}>{t.title}</Typography>
            </Box>
            <Typography sx={{ fontSize: { xs: "13.5px", sm: "12px" }, color: MUTED, fontFamily: F, lineHeight: 1.5 }}>{t.desc}</Typography>
          </Box>
        ))}
      </Box>

      <Typography sx={{ mt: 2.2, mb: 1, alignSelf: "flex-start", fontSize: { xs: "11.5px", sm: "10px" }, fontWeight: 800, letterSpacing: "0.16em", textTransform: "uppercase", color: TEAL, fontFamily: F }}>
        Where this goes next
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 1.1, width: "100%" }}>
        {NEXT_STEPS.map((t) => (
          <Box key={t.title} sx={{ px: 1.6, py: 1.3, borderRadius: "16px", border: `1px solid ${TEAL}30`, bgcolor: `${TEAL}06`, textAlign: "left" }}>
            <Typography sx={{ fontSize: { xs: "14px", sm: "12.5px" }, fontWeight: 700, color: INK, fontFamily: F, mb: 0.4, lineHeight: 1.3 }}>{t.title}</Typography>
            <Typography sx={{ fontSize: { xs: "13px", sm: "11.5px" }, color: MUTED, fontFamily: F, lineHeight: 1.5 }}>{t.desc}</Typography>
          </Box>
        ))}
      </Box>

      {authState === "loggedIn" ? (
        <Box onClick={onExit} sx={{ mt: 2.2, alignSelf: "center", display: "inline-flex", alignItems: "center", gap: 0.9, cursor: "pointer", px: 2.2, py: 1, borderRadius: "999px", border: `1.5px solid ${TEAL}`, bgcolor: PAPER, transition: "all 0.18s ease", "&:hover": { bgcolor: `${TEAL}0d` } }}>
          <Typography sx={{ fontSize: "13px", fontWeight: 700, color: INK, fontFamily: F, lineHeight: 1 }}>Explore the data in District Profiles</Typography>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
        </Box>
      ) : (
        <Box sx={{ mt: 2.2, width: "100%", maxWidth: 380, alignSelf: "center" }}>
          <DemoSignupOverlay embedded />
        </Box>
      )}

      <Typography sx={{ mt: 1.25, fontSize: { xs: "11.5px", sm: "9.5px" }, color: MUTED_LIGHT, fontFamily: F, lineHeight: 1.5, alignSelf: "center", textAlign: "center" }}>
        MRP estimates, n over 8,000 per wave. Local and demographic figures are modelled, not measured.
      </Typography>
    </>
  );
}

function DemoSignupOverlay({ embedded = false }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch("/api/user/demo-signup/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Something went wrong");
      setSuccess(data?.verify_url ? `Dev link: ${data.verify_url}` : true);
    } catch (err) {
      setError(err.message || "Unable to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const content = (
    <Box sx={{ width: embedded ? "100%" : "min(420px, 90%)", bgcolor: "#fff", borderRadius: embedded ? "0" : "16px", p: embedded ? 0 : 3.5 }}>
      {success ? (
        <>
          <Typography sx={{ fontSize: "22px", fontWeight: 800, color: INK, fontFamily: F, mb: 1 }}>Check your email</Typography>
          <Typography sx={{ fontSize: "14px", color: MUTED, fontFamily: F, lineHeight: 1.6 }}>
            {typeof success === "string" ? success : "Demo account created. Check your email to verify and activate access."}
          </Typography>
        </>
      ) : (
        <>
          <Typography sx={{ fontSize: "22px", fontWeight: 800, color: INK, fontFamily: F, mb: 0.5 }}>Get demo access</Typography>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <TextField size="small" label="Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontFamily: F } }} />
            <TextField size="small" label="Email" type="email" required value={email} onChange={(e) => { setEmail(e.target.value); setError(""); }} fullWidth sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontFamily: F } }} />
            <TextField size="small" label="Password" type="password" required value={password} onChange={(e) => { setPassword(e.target.value); setError(""); }} fullWidth sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontFamily: F } }} />
            {error && <Typography sx={{ fontSize: "12px", color: "#e11d48", fontFamily: F }}>{error}</Typography>}
            <Button type="submit" variant="contained" disableElevation disabled={submitting || !email || !password}
              sx={{ bgcolor: TEAL, color: "#fff", fontFamily: F, fontWeight: 700, fontSize: "14px", textTransform: "none", borderRadius: "10px", py: 1.1, boxShadow: `0 4px 20px ${TEAL_GLOW}`, "&:hover": { bgcolor: TEAL_DARK }, "&:disabled": { bgcolor: `${TEAL}55`, color: "#fff" } }}>
              {submitting ? "Creating account..." : "Get demo access"}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );

  if (embedded) return content;
  return (
    <Box sx={{ position: "absolute", inset: 0, zIndex: 20, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(15,23,42,0.55)", backdropFilter: "blur(6px)" }}>
      {content}
    </Box>
  );
}

const AppPanelWrapper = React.forwardRef(function AppPanelWrapper({ interactive = false }, ref) {
  const noopNavigate = useCallback(() => {}, []);
  return (
    <MainPage
      ref={ref}
      slug="aiboom"
      navigate={noopNavigate}
      hideChrome
      skipAuthRedirect
      disableMapInteraction={!interactive}
      geoPreloadOrder={["cd"]}
    />
  );
});

// ─── NarrativeCard ────────────────────────────────────────────────────────────
function NarrativeCard(props) {
  const { step, index, total, isActive, cardRef, authState, onScrollNext } = props;
  const navigate = useNavigate();
  const nextStep = STEPS[index + 1];

  const isIntroHero = step.name === "intro";
  const isSummaryHero = step.name === "summary";
  const isOverlayHero = !!step.hero;
  const heroMaxWidth = isIntroHero ? "min(1080px, 96vw)" : "min(980px, 94vw)";

  const cardSx = {
    height: { xs: "auto", sm: "100vh" },
    minHeight: { xs: isIntroHero ? "100dvh" : "80vh", sm: "auto" },
    flexShrink: 0,
    scrollSnapAlign: "start",
    scrollSnapStop: "always",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    pt: { xs: 3.5, sm: isOverlayHero ? 0 : "7vh", md: isOverlayHero ? 0 : "9vh" },
    pb: { xs: 5, sm: 5, md: 7 },
    px: { xs: 2.5, sm: 3.5, md: 5 },
    position: "relative",
    overflowY: { xs: "visible", sm: isOverlayHero ? "hidden" : "auto" },
    borderLeft: "2px solid",
    borderColor: isOverlayHero ? "transparent" : (isActive ? `${TEAL}28` : "transparent"),
    opacity: isOverlayHero ? 1 : (isActive ? 1 : 0.38),
    transition: "opacity 0.4s ease, border-color 0.4s ease",
    background: isOverlayHero ? "transparent" : (isActive ? `linear-gradient(90deg, ${TEAL}05 0%, transparent 20%)` : "transparent"),
  };

  const inner = (() => {
    switch (step.name) {
      case "intro":        return <IntroCard step={step} onScrollNext={onScrollNext} />;
      case "trend":        return <TrendCard step={step} isActive={isActive} measure={props.measure} onMeasureChange={props.onMeasureChange} />;
      case "generations":  return <GenerationsCard step={step} measure={props.measure} onMeasureChange={props.onMeasureChange} rowIdx={props.rowIdx} onRowChange={props.onRowChange} />;
      case "datacenters":  return <DataCentersCard step={step} isActive={isActive} spotIdx={props.spotIdx} onSpotChange={props.onSpotChange} highlightId={props.highlightId} onHighlight={props.onHighlight} />;
      case "reasons":      return <ReasonsCard step={step} isActive={isActive} side={props.side} onSideChange={props.onSideChange} />;
      case "authenticity": return <AuthenticityCard step={step} isActive={isActive} />;
      case "summary":      return <SummaryCard step={step} authState={authState} onExit={() => exitHighlights(navigate)} />;
      default: return <><Headline>{step.headline}</Headline>{step.copy && <BodyCopy>{step.copy}</BodyCopy>}</>;
    }
  })();

  if (isOverlayHero) {
    return (
      <Box ref={cardRef} data-index={index} sx={cardSx}>
        {isActive && (
          <Box sx={{ display: "flex", position: { xs: "relative", sm: "fixed" }, inset: { xs: "auto", sm: 0 }, zIndex: 20, height: { xs: "auto", sm: "100vh" }, alignItems: "center", justifyContent: "center", px: { xs: 0, sm: 3, md: 5 }, py: { xs: 0, sm: 3 }, pointerEvents: "none" }}>
            <Panel
              data-hero-scroll="true"
              sx={{ maxWidth: { xs: "100%", sm: heroMaxWidth }, mx: "auto", maxHeight: { xs: "none", sm: "calc(100vh - 40px)" }, overflowY: { xs: "visible", sm: "auto" }, borderRadius: "22px", p: { xs: 2.5, sm: 3.5, md: isIntroHero ? 4 : 3.5 }, textAlign: isIntroHero ? "center" : "left", pointerEvents: "auto", bgcolor: "rgba(255,255,255,0.85)", backgroundImage: "none", backdropFilter: "blur(20px)", border: "1px solid rgba(15,23,42,0.06)", boxShadow: "0 26px 64px rgba(15,23,42,0.08)", display: "flex", flexDirection: "column" }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", width: "100%", animation: "cardEnter 0.6s cubic-bezier(0.22,1,0.36,1) both", "@keyframes cardEnter": { from: { opacity: 0.3, transform: "translateY(10px)" }, to: { opacity: 1, transform: "translateY(0)" } } }}>
                {inner}
                {!isSummaryHero && <StepHeader index={index} total={total} chapter={CHAPTER_NAMES[step.name]} isActive={isActive} hasNext={!!nextStep} onScrollNext={onScrollNext} />}
              </Box>
            </Panel>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box ref={cardRef} data-index={index} sx={cardSx}>
      <Box key={isActive ? "active" : "idle"} sx={{ display: "flex", flexDirection: "column", flex: 1, width: "100%", animation: isActive ? "cardEnter 0.45s cubic-bezier(0.22,1,0.36,1) both" : "none", "@keyframes cardEnter": { from: { opacity: 0.3, transform: "translateY(10px)" }, to: { opacity: 1, transform: "translateY(0)" } } }}>
        {inner}
        <StepHeader index={index} total={total} chapter={CHAPTER_NAMES[step.name]} isActive={isActive} hasNext={!!nextStep} onScrollNext={onScrollNext} />
      </Box>
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function AIBoomPage() {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [authState, setAuthState] = useState("guest");
  const [measure, setMeasure] = useState("society");
  const [rowIdx, setRowIdx] = useState(0);
  const [spotIdx, setSpotIdx] = useState(0);
  const [side, setSide] = useState("oppose");
  const [highlightId, setHighlightId] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const [labelExpanded, setLabelExpanded] = useState(false);

  const cardRefs = useRef([]);
  const mainRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const navTimerRef = useRef(null);
  const visibleSet = useRef(new Set());
  const activeIndexRef = useRef(0);
  const mapPanelRef = useRef(null);

  const isHeroStep = !!STEPS[activeIndex]?.hero;
  const activeStep = STEPS[activeIndex];

  useEffect(() => {
    fetch("/api/user-info/")
      .then((r) => r.json())
      .then((d) => setAuthState(d?.email ? "loggedIn" : "loggedOut"))
      .catch(() => setAuthState("guest"));
  }, []);

  // Per-step resets, so a selection never leaks between slides.
  useEffect(() => {
    const n = STEPS[activeIndex]?.name;
    if (n !== "generations" && n !== "trend") setMeasure("society");
    if (n !== "generations") setRowIdx(0);
    if (n !== "datacenters") { setSpotIdx(0); setHighlightId(null); }
    if (n !== "reasons") setSide("oppose");
  }, [activeIndex]);

  // One source of truth for the map's extra state, so every control writes the
  // FULL state rather than only the dimension it knows about. Demographic keys
  // are always written, null when unused, so a cut cannot survive into a slide
  // that does not want one.
  const extraMapStateFor = useCallback((step, over = {}) => {
    const meas = over.measure ?? measure;
    const rI = over.rowIdx ?? rowIdx;
    const noDemos = { gender: null, age: null, vote24: null, education: null, ethnicity: null };
    if (step?.name === "trend") {
      return { ...noDemos, variable: meas === "life" ? "ai_impact_life" : "ai_impact_society" };
    }
    if (step?.name === "generations") {
      const rows = AI_DIST[meas].rows.filter((r) => !r.ref);
      return {
        ...noDemos,
        variable: meas === "life" ? "ai_impact_life" : "ai_impact_society",
        ...(rows[rI]?.filter || {}),
      };
    }
    return noDemos;
  }, [measure, rowIdx]);

  const extraMapStateRef = useRef(() => ({}));
  useEffect(() => {
    extraMapStateRef.current = () => extraMapStateFor(STEPS[activeIndexRef.current]);
  }, [extraMapStateFor]);

  const applyMapState = useCallback((over) => {
    const step = STEPS[activeIndexRef.current];
    if (!mainRef.current || !step?.mapView) return;
    armCrossfade(getGlMap(mainRef.current));
    mainRef.current.setHighlightsState({ ...step.mapView, ...extraMapStateFor(step, over) });
  }, [extraMapStateFor]);

  // Return the map to the active step's camera. A highlight zoom is one-way:
  // MapPage zooms when the highlight array is non-empty and does nothing when
  // it empties, so clearing has to fly back explicitly.
  const clearZoom = useCallback(() => {
    const step = STEPS[activeIndexRef.current];
    if (step?.flyTo) mainRef.current?.flyTo?.(flyForViewport(step.flyTo));
    invalidateFlyCache();
  }, []);

  const handleMeasureChange = useCallback((k) => { setMeasure(k); setRowIdx(0); applyMapState({ measure: k, rowIdx: 0 }); }, [applyMapState]);
  const handleRowChange = useCallback((i) => { setRowIdx(i); applyMapState({ rowIdx: i }); }, [applyMapState]);

  // The reasons toggle swaps which battery the map draws as well as the card.
  const handleSideChange = useCallback((k) => {
    setSide(k);
    if (!mainRef.current) return;
    armCrossfade(getGlMap(mainRef.current));
    mainRef.current.setHighlightsState({
      variable: k === "oppose" ? "pol_dc_oppose_reasons_local" : "pol_dc_support_reasons_local",
      geoLevel: "cd",
      preferenceFilter: "top",
      viewMode: "top",
      topStrengthShading: false,
    });
  }, []);

  const handleHighlight = useCallback((id) => {
    setHighlightId(id);
    mainRef.current?.setState?.({ chatHighlightAreas: id ? [id] : [] });
    invalidateFlyCache();
    if (!id) clearZoom();
  }, [clearZoom]);

  // Switching state clears an outline that is about to leave the list.
  const handleSpotChange = useCallback((i) => {
    setSpotIdx(i);
    if (highlightId) {
      setHighlightId(null);
      mainRef.current?.setState?.({ chatHighlightAreas: [] });
      clearZoom();
    }
  }, [highlightId, clearZoom]);

  const targetIndexRef = useRef(null);
  const unlockTimerRef = useRef(null);
  useEffect(() => () => { if (unlockTimerRef.current) clearTimeout(unlockTimerRef.current); }, []);

  const scrollToIndex = useCallback((i) => {
    const card = cardRefs.current[i];
    if (!card) return;
    const container = scrollContainerRef.current;
    if (container) container._scrollLocked = true;
    targetIndexRef.current = i;
    activeIndexRef.current = i;
    visibleSet.current = new Set([i]);
    setActiveIndex(i);
    card.scrollIntoView({ behavior: "smooth", block: "start" });
    if (unlockTimerRef.current) clearTimeout(unlockTimerRef.current);
    unlockTimerRef.current = window.setTimeout(() => {
      unlockTimerRef.current = null;
      if (container) container._scrollLocked = false;
      targetIndexRef.current = null;
      const el = cardRefs.current[i];
      if (!el) return;
      if (window.innerWidth >= 600 && container) {
        const offset = el.getBoundingClientRect().top - container.getBoundingClientRect().top;
        if (Math.abs(offset) > 4) el.scrollIntoView({ behavior: "auto", block: "start" });
      }
    }, 1200);
  }, []);

  const getNearestCardIndex = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || !cardRefs.current.length) return null;
    const containerTop = container.getBoundingClientRect().top;
    let nearestIdx = null;
    let nearestDistance = Infinity;
    cardRefs.current.forEach((card, idx) => {
      if (!card) return;
      const distance = Math.abs(card.getBoundingClientRect().top - containerTop);
      if (distance < nearestDistance) { nearestDistance = distance; nearestIdx = idx; }
    });
    return nearestIdx;
  }, []);

  // Sliding cooldown, so one physical flick advances exactly one slide.
  const wheelCooldownRef = useRef(false);
  const wheelDeltaRef = useRef(0);
  const lastWheelTsRef = useRef(0);
  const wheelNavAtRef = useRef(0);

  const handleWheel = useCallback((e) => {
    if (window.innerWidth < 600) return;
    const container = scrollContainerRef.current;
    if (!container) return;

    // Let the centred hero panel scroll internally until it bottoms out.
    const heroEl = e.target instanceof Element ? e.target.closest("[data-hero-scroll]") : null;
    if (heroEl instanceof HTMLElement) {
      const canDown = heroEl.scrollTop + heroEl.clientHeight < heroEl.scrollHeight - 2;
      const canUp = heroEl.scrollTop > 2;
      if ((e.deltaY > 0 && canDown) || (e.deltaY < 0 && canUp)) { wheelDeltaRef.current = 0; return; }
    }
    const cardEl = e.target instanceof Element ? e.target.closest("[data-index]") : null;
    if (cardEl instanceof HTMLElement) {
      const canDown = cardEl.scrollTop + cardEl.clientHeight < cardEl.scrollHeight - 2;
      const canUp = cardEl.scrollTop > 2;
      if ((e.deltaY > 0 && canDown) || (e.deltaY < 0 && canUp)) { wheelDeltaRef.current = 0; return; }
    }

    e.preventDefault();
    const now = Date.now();
    const gap = now - lastWheelTsRef.current;
    lastWheelTsRef.current = now;
    if (container._scrollLocked) return;
    if (wheelCooldownRef.current) {
      const sinceNav = now - wheelNavAtRef.current;
      if (gap < 250 && sinceNav < 1600) return;
      wheelCooldownRef.current = false;
    }

    wheelDeltaRef.current += e.deltaY;
    if (Math.abs(wheelDeltaRef.current) < 70) return;

    const currentIdx = targetIndexRef.current ?? getNearestCardIndex();
    if (currentIdx == null) return;
    const dir = wheelDeltaRef.current > 0 ? 1 : -1;
    const nextIdx = Math.max(0, Math.min(STEPS.length - 1, currentIdx + dir));
    if (nextIdx === currentIdx) { wheelDeltaRef.current = 0; return; }

    wheelDeltaRef.current = 0;
    wheelCooldownRef.current = true;
    wheelNavAtRef.current = Date.now();
    scrollToIndex(nextIdx);
  }, [getNearestCardIndex, scrollToIndex]);

  // React's onWheel is passive, so preventDefault inside it is ignored and
  // native inertia scrolls alongside the programmatic snap. Attach ourselves.
  useEffect(() => {
    const els = [scrollContainerRef.current, mapPanelRef.current].filter(Boolean);
    els.forEach((el) => el.addEventListener("wheel", handleWheel, { passive: false }));
    return () => els.forEach((el) => el.removeEventListener("wheel", handleWheel));
  }, [handleWheel]);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
    if (navTimerRef.current) clearTimeout(navTimerRef.current);
    navTimerRef.current = setTimeout(() => {
      navTimerRef.current = null;
      const stepName = STEPS[activeIndex]?.name;
      if (stepName) navigateToStep(mainRef.current, stepName, extraMapStateRef.current());
    }, 160);
  }, [activeIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let fired = false;
    const poll = setInterval(() => {
      const inst = mainRef.current;
      if (!inst) return;
      const mapPage = inst._mapPageInstance;
      const cdLoaded = mapPage?.state?.geoPreloadStatus?.cd === "loaded";
      if (!fired && inst.state?.mapLoading === false && cdLoaded) {
        const featureCount = mapPage?.state?.geoData?.features?.length ?? 0;
        // Recover a blank map: if the visible geometry is not the CD layer (the
        // mount-time states load can resolve AFTER the cd load and overwrite
        // geoData), reload cd. loadGeo is cache-first so this is cheap when
        // already cached.
        //
        // This used to be gated on _geoCache.has("cd:"), which NEVER matched and
        // so disabled the recovery entirely, leaving the reel stuck on a basemap
        // with no districts. CD cache keys carry the boundary vintage and the
        // mobile marker (MapPage builds "cd::CD120", or "cd::CD120:m" on a
        // phone), so a literal "cd:" lookup is always false. Same fix as
        // NJICreativePage and SelfDrivingPage.
        if (featureCount < 200 && mapPage) {
          mapPage.loadGeo("cd", null, { doFit: false });
        }
        fired = true;
        clearInterval(poll);
        clearTimeout(fallback);
        setMapReady(true);
        const stepName = STEPS[activeIndexRef.current]?.name;
        if (stepName) navigateToStep(inst, stepName, extraMapStateRef.current());
      }
    }, 150);
    const fallback = setTimeout(() => setMapReady(true), 30000);
    return () => { clearInterval(poll); clearTimeout(fallback); };
  }, []);

  // Mapbox does not observe CSS size changes. resizeOnly is cheap and safe to
  // fire mid-transition; refit also re-applies the camera so it fires once at
  // the end.
  const withMap = useCallback((fn) => {
    let tries = 0;
    const attempt = () => {
      const map = getGlMap(mainRef.current);
      if (!map) { if (tries++ < 20) setTimeout(attempt, 100); return; }
      fn(map);
    };
    attempt();
  }, []);
  const resizeOnly = useCallback(() => { withMap((m) => m.resize()); }, [withMap]);
  const refit = useCallback(() => {
    withMap((m) => {
      m.resize();
      const step = STEPS[activeIndexRef.current];
      if (step?.flyTo) mainRef.current?.flyTo?.(flyForViewport(step.flyTo));
    });
  }, [withMap]);

  // DESKTOP: the panel animates between full width on the heroes and 56% on
  // content slides. Without this the canvas keeps its old width for the rest of
  // the reel and the projection is wrong.
  useEffect(() => {
    if (window.innerWidth < 600) return;
    const t1 = setTimeout(resizeOnly, 100);
    const t2 = setTimeout(resizeOnly, 300);
    const t3 = setTimeout(refit, 520);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [isHeroStep, resizeOnly, refit]);

  useEffect(() => {
    if (window.innerWidth >= 600) return;
    const raf = requestAnimationFrame(refit);
    const settle = setTimeout(refit, 350);
    let debounce;
    let ro;
    const el = mapPanelRef.current;
    if (el && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => { clearTimeout(debounce); debounce = setTimeout(refit, 150); });
      ro.observe(el);
    }
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(settle);
      clearTimeout(debounce);
      if (ro) ro.disconnect();
    };
  }, [refit]);

  useEffect(() => {
    if (window.innerWidth >= 600) return;
    refit();
    const t = setTimeout(refit, 350);
    return () => clearTimeout(t);
  }, [mapFullscreen, refit]);

  useEffect(() => {
    let t;
    const onResize = () => { clearTimeout(t); t = setTimeout(refit, 180); };
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); clearTimeout(t); };
  }, [refit]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (window.innerWidth < 600) {
          entries.forEach((entry) => {
            const idx = Number(entry.target.dataset.index);
            if (entry.isIntersecting) visibleSet.current.add(idx);
            else visibleSet.current.delete(idx);
          });
          if (visibleSet.current.size > 0) setActiveIndex(Math.max(...visibleSet.current));
        } else {
          const nearestIdx = getNearestCardIndex();
          if (nearestIdx != null) setActiveIndex(nearestIdx);
        }
      },
      { root: window.innerWidth < 600 ? null : scrollContainerRef.current, rootMargin: "0px 0px -40% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    cardRefs.current.forEach((card) => { if (card) observer.observe(card); });
    return () => observer.disconnect();
  }, [getNearestCardIndex]);

  const progress = (activeIndex / (STEPS.length - 1)) * 100;

  const mapLabel = (() => {
    if (activeStep?.name === "trend") {
      return { text: `${AI_TREND[measure].label} · net by district`, color: AI_TREND[measure].color };
    }
    if (activeStep?.name === "reasons") {
      return { text: `Leading local reason · ${REASONS[side].label.toLowerCase()}`, color: REASONS[side].tone };
    }
    if (activeStep?.name === "generations") {
      const rows = AI_DIST[measure].rows.filter((r) => !r.ref);
      return { text: `${AI_TREND[measure].label} · ${rows[rowIdx]?.name ?? ""}`, color: AI_TREND[measure].color };
    }
    return { text: activeStep?.mapLabel, color: null };
  })();

  return (
    <Box sx={{ bgcolor: BG, height: { xs: "auto", sm: "100vh" }, overflow: { xs: "visible", sm: "hidden" } }}>
      {/* Minimal chrome: a hairline progress line and a floating exit. */}
      <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1100, display: "flex", alignItems: "center", px: 2, py: 1, pointerEvents: "none" }}>
        <Box role="progressbar" aria-valuenow={Math.round(progress)} sx={{ height: 3, position: "absolute", top: 0, left: 0, bgcolor: TEAL, width: `${progress}%`, transition: "width 0.4s ease" }} />
        <Box onClick={() => exitHighlights(navigate)} sx={{ ml: "auto", width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "8px", cursor: "pointer", pointerEvents: "auto", color: "rgba(15,23,42,0.45)", bgcolor: "rgba(255,255,255,0.8)", backdropFilter: "blur(8px)", border: "1px solid rgba(15,23,42,0.06)", "&:hover": { color: TEAL }, transition: "all 0.15s ease" }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </Box>
      </Box>

      <Box ref={mapPanelRef} sx={{ position: "fixed", top: 0, left: { xs: 0, sm: "auto" }, right: 0, width: { xs: "100%", sm: isHeroStep ? "100%" : "52%", md: isHeroStep ? "100%" : "56%" }, height: { xs: mapFullscreen ? "100dvh" : "40dvh", sm: "100vh" }, zIndex: { xs: mapFullscreen ? 1300 : 5, sm: 5 }, overflow: "hidden", borderLeft: { sm: isHeroStep ? "none" : "0.5px solid rgba(15,23,42,0.06)" }, bgcolor: "background.paper", transition: "width 0.45s ease, height 0.3s ease, top 0.3s ease" }}>
        <Box sx={{ height: "100%", position: "relative", overflow: "hidden" }}>
          <Box sx={{ height: "100%", filter: isHeroStep ? "blur(10px) saturate(0.8)" : "none", transform: isHeroStep ? "scale(1.04)" : "none", transition: "filter 0.5s ease, transform 0.5s ease" }}>
            <AppPanelWrapper ref={mainRef} interactive={mapFullscreen} />
          </Box>

          {isHeroStep && (
            <Box sx={{ position: "absolute", inset: 0, bgcolor: "rgba(255,255,255,0.3)", backgroundImage: "linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.5) 100%)", pointerEvents: "auto", zIndex: 2 }} />
          )}

          <Box sx={{ position: "absolute", inset: 0, zIndex: 8, cursor: "default", pointerEvents: "none" }} />

          <Box sx={{ position: "absolute", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#ffffff", opacity: mapReady ? 0 : 1, pointerEvents: mapReady ? "none" : "auto", transition: "opacity 0.6s ease" }}>
            <CircularProgress size={28} thickness={3.5} sx={{ color: TEAL }} />
          </Box>

          {activeStep?.mapHoverHint && !isHeroStep && <MapHint />}

          <Box sx={{ position: "absolute", top: { xs: 54, sm: 12 }, left: 12, zIndex: 10, pointerEvents: { xs: isHeroStep ? "none" : "auto", sm: "none" }, opacity: isHeroStep ? 0 : 1, transition: "opacity 0.25s ease", maxWidth: "calc(100% - 130px)" }}>
            <Box key={mapLabel.text} onClick={() => setLabelExpanded((v) => !v)} sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, px: 1.5, py: 0.65, borderRadius: "8px", bgcolor: "rgba(255,255,255,0.93)", backdropFilter: "blur(10px)", border: `1px solid ${TEAL}20`, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", cursor: { xs: "pointer", sm: "default" }, WebkitTapHighlightColor: "transparent" }}>
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: mapLabel.color || `linear-gradient(135deg, ${TEAL} 0%, ${ACCENT} 100%)`, flexShrink: 0 }} />
              <Typography sx={{ display: { xs: labelExpanded ? "inline" : "none", sm: "inline" }, fontSize: "11px", fontWeight: 600, color: INK, fontFamily: F, lineHeight: 1.3 }}>
                {mapLabel.text}
              </Typography>
            </Box>
          </Box>

          <Box onClick={() => setMapFullscreen((f) => !f)} sx={{ display: { xs: "flex", sm: "none" }, position: "absolute", bottom: 12, right: 12, zIndex: 20, width: 36, height: 36, borderRadius: "10px", bgcolor: "rgba(255,255,255,0.92)", backdropFilter: "blur(8px)", border: `1px solid ${TEAL}33`, boxShadow: "0 2px 12px rgba(0,0,0,0.08)", alignItems: "center", justifyContent: "center", cursor: "pointer", userSelect: "none", WebkitTapHighlightColor: "transparent" }}>
            {mapFullscreen ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: { xs: "none", sm: "flex" }, flexDirection: "column", alignItems: "center", gap: 1, position: "fixed", left: 14, top: "50%", transform: "translateY(-50%)", zIndex: 1050, transition: "opacity 0.4s ease" }}>
        {STEPS.map((s, i) => (
          <Box key={s.name} onClick={() => scrollToIndex(i)} title={s.headline?.slice(0, 60)} sx={{ width: activeIndex === i ? 8 : 5, height: activeIndex === i ? 8 : 5, borderRadius: "50%", bgcolor: activeIndex === i ? TEAL : `${TEAL}30`, transition: "all 0.25s ease", cursor: "pointer", "&:hover": { bgcolor: TEAL, transform: "scale(1.35)" } }} />
        ))}
      </Box>

      {activeIndex === 0 && (
        <Box sx={{ display: { xs: "flex", sm: "none" }, position: "fixed", bottom: "calc(54px + env(safe-area-inset-bottom) + 10px)", left: "50%", transform: "translateX(-50%)", zIndex: 1040, alignItems: "center", gap: 0.75, px: 1.75, py: 0.6, borderRadius: "999px", bgcolor: "rgba(255,255,255,0.96)", backdropFilter: "blur(8px)", border: `1px solid ${TEAL}22`, boxShadow: "0 2px 12px rgba(0,0,0,0.10)", pointerEvents: "none" }}>
          <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: TEAL, fontFamily: F }}>Scroll</Typography>
          <Box sx={{ display: "flex", animation: "nudgeDown 1.6s ease-in-out infinite", "@keyframes nudgeDown": { "0%,100%": { transform: "translateY(0)", opacity: 0.6 }, "50%": { transform: "translateY(3px)", opacity: 1 } } }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </Box>
        </Box>
      )}

      <Box sx={{ display: { xs: "flex", sm: "none" }, position: "fixed", bottom: 0, left: 0, right: 0, height: 34, bgcolor: "rgba(255,255,255,0.92)", backdropFilter: "blur(10px)", borderTop: "0.5px solid rgba(15,23,42,0.06)", zIndex: 1050, alignItems: "center", justifyContent: "space-between", px: 2 }}>
        <Box onClick={() => scrollToIndex(Math.max(0, activeIndex - 1))} sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: activeIndex === 0 ? "default" : "pointer", opacity: activeIndex === 0 ? 0.25 : 1 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
          <Typography sx={{ fontSize: "11px", fontWeight: 600, color: TEAL, fontFamily: F }}>Prev</Typography>
        </Box>
        <Typography sx={{ fontSize: "10px", fontWeight: 600, color: MUTED, fontFamily: F, letterSpacing: "0.05em" }}>{activeIndex + 1} / {STEPS.length}</Typography>
        <Box onClick={() => scrollToIndex(Math.min(STEPS.length - 1, activeIndex + 1))} sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: activeIndex === STEPS.length - 1 ? "default" : "pointer", opacity: activeIndex === STEPS.length - 1 ? 0.25 : 1 }}>
          <Typography sx={{ fontSize: "11px", fontWeight: 600, color: TEAL, fontFamily: F }}>Next</Typography>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
        </Box>
      </Box>

      <Box ref={scrollContainerRef} sx={{ mr: { xs: 0, sm: isHeroStep ? 0 : "52%", md: isHeroStep ? 0 : "56%" }, pt: { xs: "40dvh", sm: 0 }, pb: { xs: "54px", sm: 0 }, height: { xs: "auto", sm: "100vh" }, overflowY: { xs: "visible", sm: "scroll" }, scrollSnapType: { xs: "none", sm: "y mandatory" }, "&::-webkit-scrollbar": { display: "none" }, msOverflowStyle: "none", scrollbarWidth: "none" }}>
        {STEPS.map((step, i) => (
          <NarrativeCard
            key={step.name}
            step={step}
            index={i}
            total={STEPS.length}
            isActive={activeIndex === i}
            cardRef={(el) => { cardRefs.current[i] = el; }}
            authState={authState}
            measure={measure}
            onMeasureChange={handleMeasureChange}
            rowIdx={rowIdx}
            onRowChange={handleRowChange}
            spotIdx={spotIdx}
            onSpotChange={handleSpotChange}
            side={side}
            onSideChange={handleSideChange}
            highlightId={highlightId}
            onHighlight={handleHighlight}
            onScrollNext={() => scrollToIndex(i + 1)}
          />
        ))}
      </Box>
    </Box>
  );
}
